import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Wallet, User, Settings, Users, LayoutDashboard, ChevronDown, Book, Gift, HelpCircle, BarChart3, CreditCard, Cpu, Rocket, Target, Briefcase, Lock, TrendingUp, Search, MessageSquare, Coins } from 'lucide-react';
import { WidgetFeaturesMegaMenu } from './canvas/WidgetFeaturesMegaMenu';
import WalletConnection from './WalletConnection';
import Notifications from './Notifications';
import { CanvasWidget } from './canvas/types';
import NotificationCenter from './NotificationCenter';
import { searchTokens } from '../services/solanatrackerService';
import { Token } from '../types/supabase';

interface TokenSearchResult {
  name: string;
  symbol: string;
  mint: string;
  decimals: number;
  image?: string;
  holders?: number;
  verified?: boolean;
  liquidityUsd: number | undefined;
  marketCapUsd: number | undefined;
  priceUsd: number | undefined;
  volume_24h?: number;
  market?: string;
  totalBuys?: number;
  totalSells?: number;
  totalTransactions?: number;
  twitter?: string;
  telegram?: string;
  website?: string;
  discord?: string;
}

interface UltimateTokenSearchProps {
  onSelectToken?: (token: Token) => void;
}

interface SavedSearch {
  id: string;
  name: string;
  query: string;
  filters: typeof filters;
  createdAt: string;
}

const UltimateTokenSearch: React.FC<UltimateTokenSearchProps> = ({ onSelectToken }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TokenSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [allResults, setAllResults] = useState<TokenSearchResult[]>([]);

  const RESULTS_PER_PAGE = 15;

  // Advanced search filters state
  const [filters, setFilters] = useState({
    // Date filters
    minCreatedAt: '',
    maxCreatedAt: '',
    
    // Financial filters
    minLiquidity: '',
    maxLiquidity: '',
    minMarketCap: '',
    maxMarketCap: '',
    
    // Volume filters (24h default)
    minVolume: '',
    maxVolume: '',
    volumeTimeframe: '24h',
    
    // Transaction filters
    minBuys: '',
    maxBuys: '',
    minSells: '',
    maxSells: '',
    minTotalTransactions: '',
    maxTotalTransactions: '',
    
    // Security/Token filters
    lpBurn: '',
    market: '',
    freezeAuthority: '',
    mintAuthority: '',
    deployer: '',
    status: '',
    
    // Display options
    showPriceChanges: true,
    sortBy: 'liquidityUsd',
    sortOrder: 'desc'
  });

  // Define SavedSearch interface here after filters is defined
  interface SavedSearchLocal {
    id: string;
    name: string;
    query: string;
    filters: typeof filters;
    createdAt: string;
  }

  // Saved searches state
  const [savedSearches, setSavedSearches] = useState<SavedSearchLocal[]>([]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');

  // Load saved searches from localStorage on component mount
  useEffect(() => {
    const loadSavedSearches = () => {
      try {
        const saved = localStorage.getItem('ekko_saved_searches');
        if (saved) {
          setSavedSearches(JSON.parse(saved));
        }
      } catch (error) {
        console.error('Error loading saved searches:', error);
      }
    };
    loadSavedSearches();
  }, []);

  // Save searches to localStorage whenever savedSearches changes
  useEffect(() => {
    try {
      localStorage.setItem('ekko_saved_searches', JSON.stringify(savedSearches));
    } catch (error) {
      console.error('Error saving searches to localStorage:', error);
    }
  }, [savedSearches]);

  // Save current search
  const saveCurrentSearch = () => {
    if (!saveSearchName.trim()) return;

    const newSearch: SavedSearchLocal = {
      id: Date.now().toString(),
      name: saveSearchName.trim(),
      query: searchQuery,
      filters: { ...filters },
      createdAt: new Date().toISOString()
    };

    setSavedSearches(prev => [newSearch, ...prev]);
    setSaveSearchName('');
    setShowSaveModal(false);
  };

  // Load a saved search
  const loadSavedSearch = (savedSearch: SavedSearchLocal) => {
    setSearchQuery(savedSearch.query);
    setFilters(savedSearch.filters);
    setShowSavedSearches(false);
    setCurrentPage(1);
    
    // Trigger search with loaded parameters
    setTimeout(() => {
      handleSearch(savedSearch.query, 1);
    }, 100);
  };

  // Delete a saved search
  const deleteSavedSearch = (searchId: string) => {
    setSavedSearches(prev => prev.filter(search => search.id !== searchId));
  };

  // Check if current search has active filters or query
  const hasActiveSearch = () => {
    const hasQuery = searchQuery.trim() !== '';
    const hasFilters = Object.entries(filters).some(([key, value]) => {
      return value !== '' && value !== 'liquidityUsd' && value !== 'desc' && value !== true && value !== '24h';
    });
    return hasQuery || hasFilters;
  };

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        setShowAdvancedFilters(false);
        setCurrentPage(1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Add keyboard shortcut listener
  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsSearchOpen(true);
      }
      
      // Close on Escape
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
        setShowAdvancedFilters(false);
        setCurrentPage(1);
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isSearchOpen]);

  const buildSearchParams = (query: string, page: number = 1) => {
    const params: any = {
      limit: 100, // Fetch more results for client-side pagination
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      showPriceChanges: filters.showPriceChanges
    };

    // Only add query if it exists
    if (query.trim()) {
      params.query = query.trim();
    }

    // Add date filters
    if (filters.minCreatedAt) {
      params.minCreatedAt = new Date(filters.minCreatedAt).getTime();
    }
    if (filters.maxCreatedAt) {
      params.maxCreatedAt = new Date(filters.maxCreatedAt).getTime();
    }

    // Add financial filters - convert string to number if not empty
    if (filters.minLiquidity && filters.minLiquidity.trim()) {
      const value = parseFloat(filters.minLiquidity);
      if (!isNaN(value)) params.minLiquidity = value;
    }
    if (filters.maxLiquidity && filters.maxLiquidity.trim()) {
      const value = parseFloat(filters.maxLiquidity);
      if (!isNaN(value)) params.maxLiquidity = value;
    }
    if (filters.minMarketCap && filters.minMarketCap.trim()) {
      const value = parseFloat(filters.minMarketCap);
      if (!isNaN(value)) params.minMarketCap = value;
    }
    if (filters.maxMarketCap && filters.maxMarketCap.trim()) {
      const value = parseFloat(filters.maxMarketCap);
      if (!isNaN(value)) params.maxMarketCap = value;
    }

    // Add volume filters with timeframe - convert string to number if not empty
    if (filters.minVolume && filters.minVolume.trim()) {
      const value = parseFloat(filters.minVolume);
      if (!isNaN(value)) params[`minVolume_${filters.volumeTimeframe}`] = value;
    }
    if (filters.maxVolume && filters.maxVolume.trim()) {
      const value = parseFloat(filters.maxVolume);
      if (!isNaN(value)) params[`maxVolume_${filters.volumeTimeframe}`] = value;
    }

    // Add transaction filters - convert string to number if not empty
    if (filters.minBuys && filters.minBuys.trim()) {
      const value = parseInt(filters.minBuys);
      if (!isNaN(value)) params.minBuys = value;
    }
    if (filters.maxBuys && filters.maxBuys.trim()) {
      const value = parseInt(filters.maxBuys);
      if (!isNaN(value)) params.maxBuys = value;
    }
    if (filters.minSells && filters.minSells.trim()) {
      const value = parseInt(filters.minSells);
      if (!isNaN(value)) params.minSells = value;
    }
    if (filters.maxSells && filters.maxSells.trim()) {
      const value = parseInt(filters.maxSells);
      if (!isNaN(value)) params.maxSells = value;
    }
    if (filters.minTotalTransactions && filters.minTotalTransactions.trim()) {
      const value = parseInt(filters.minTotalTransactions);
      if (!isNaN(value)) params.minTotalTransactions = value;
    }
    if (filters.maxTotalTransactions && filters.maxTotalTransactions.trim()) {
      const value = parseInt(filters.maxTotalTransactions);
      if (!isNaN(value)) params.maxTotalTransactions = value;
    }

    // Add security/token filters
    if (filters.lpBurn && filters.lpBurn.trim()) {
      const value = parseInt(filters.lpBurn);
      if (!isNaN(value)) params.lpBurn = value;
    }
    if (filters.market) params.market = filters.market;
    if (filters.freezeAuthority) params.freezeAuthority = filters.freezeAuthority;
    if (filters.mintAuthority) params.mintAuthority = filters.mintAuthority;
    if (filters.deployer) params.deployer = filters.deployer;
    if (filters.status) params.status = filters.status;

    return params;
  };

  const handleSearch = async (query: string, page: number = 1) => {
    // Check if we have any active filters
    const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
      return value !== '' && value !== 'liquidityUsd' && value !== 'desc' && value !== true && value !== '24h';
    });

    // If no query and no filters, don't search
    if (!query.trim() && !hasActiveFilters) {
      setSearchResults([]);
      setAllResults([]);
      setCurrentPage(1);
      setTotalResults(0);
      setHasNextPage(false);
      return;
    }

    setIsLoading(true);
    try {
      const searchParams = buildSearchParams(query, page);
      console.log('🔍 Ultimate Token Search params:', searchParams);
      
      const result = await searchTokens(searchParams);

      if (result.status === 'success' && Array.isArray(result.data)) {
        // Store all results for client-side pagination
        setAllResults(result.data);
        setTotalResults(result.data.length);
        
        // Calculate pagination
        const startIndex = (page - 1) * RESULTS_PER_PAGE;
        const endIndex = startIndex + RESULTS_PER_PAGE;
        const pageResults = result.data.slice(startIndex, endIndex);
        
        setSearchResults(pageResults);
        setCurrentPage(page);
        setHasNextPage(endIndex < result.data.length);
      } else {
        setSearchResults([]);
        setAllResults([]);
        setCurrentPage(1);
        setTotalResults(0);
        setHasNextPage(false);
      }
    } catch (error) {
      console.error('Token search error:', error);
      setSearchResults([]);
      setAllResults([]);
      setCurrentPage(1);
      setTotalResults(0);
      setHasNextPage(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on new search

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value, 1);
    }, 300);
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));

    setCurrentPage(1); // Reset to first page when filters change

    // Re-trigger search immediately if there's a query OR if we have active filters
    const newFilters = { ...filters, [key]: value };
    const hasActiveFilters = Object.entries(newFilters).some(([filterKey, filterValue]) => {
      return filterValue !== '' && filterValue !== 'liquidityUsd' && filterValue !== 'desc' && filterValue !== true && filterValue !== '24h';
    });

    if (searchQuery.trim() || hasActiveFilters) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(searchQuery, 1);
      }, 500);
    }
  };

  const handleSearchByFilters = () => {
    setCurrentPage(1);
    handleSearch('', 1);
  };

  const handleNextPage = () => {
    if (hasNextPage && allResults.length > 0) {
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * RESULTS_PER_PAGE;
      const endIndex = startIndex + RESULTS_PER_PAGE;
      const pageResults = allResults.slice(startIndex, endIndex);
      
      setSearchResults(pageResults);
      setCurrentPage(nextPage);
      setHasNextPage(endIndex < allResults.length);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1 && allResults.length > 0) {
      const prevPage = currentPage - 1;
      const startIndex = (prevPage - 1) * RESULTS_PER_PAGE;
      const endIndex = startIndex + RESULTS_PER_PAGE;
      const pageResults = allResults.slice(startIndex, endIndex);
      
      setSearchResults(pageResults);
      setCurrentPage(prevPage);
      setHasNextPage(endIndex < allResults.length);
    }
  };

  const clearFilters = () => {
    setFilters({
      minCreatedAt: '',
      maxCreatedAt: '',
      minLiquidity: '',
      maxLiquidity: '',
      minMarketCap: '',
      maxMarketCap: '',
      minVolume: '',
      maxVolume: '',
      volumeTimeframe: '24h',
      minBuys: '',
      maxBuys: '',
      minSells: '',
      maxSells: '',
      minTotalTransactions: '',
      maxTotalTransactions: '',
      lpBurn: '',
      market: '',
      freezeAuthority: '',
      mintAuthority: '',
      deployer: '',
      status: '',
      showPriceChanges: true,
      sortBy: 'liquidityUsd',
      sortOrder: 'desc'
    });

    setCurrentPage(1);

    // Clear search results when clearing filters if no query
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setAllResults([]);
      setTotalResults(0);
      setHasNextPage(false);
    } else {
      // Re-trigger search if there's a query
      handleSearch(searchQuery, 1);
    }
  };

  const handleSelectToken = (result: TokenSearchResult) => {
    // Convert TokenSearchResult to Token format
    const token: Token = {
      id: result.mint,
      mint: result.mint,
      symbol: result.symbol,
      name: result.name,
      image_uri: result.image || null,
      image_url: result.image || null,
      price_usd: result.priceUsd || null,
      market_cap_usd: result.marketCapUsd || null,
      liquidity_usd: result.liquidityUsd || null,
      volume_usd: result.volume_24h || null,
      holders_count: result.holders || null,
      // Set other required fields with defaults
      signature: null,
      trader_public_key: null,
      tx_type: null,
      sol_amount: null,
      initial_buy: null,
      market_cap_sol: null,
      token_amount: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      timestamp: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      description: null,
      twitter: result.twitter || null,
      telegram: result.telegram || null,
      website: result.website || null,
      discord: result.discord || null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: result.totalBuys || null,
      sells: result.totalSells || null,
      holders: result.holders || null,
      volume: result.volume_24h || null,
      liquidity: result.liquidityUsd || null,
      token_price: result.priceUsd || null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      tx_volume: result.totalTransactions || null,
      buys_count: result.totalBuys || null,
      sells_count: result.totalSells || null,
      position: null,
      market_type: null,
      market: result.market || null,
      userBalance: null,
      userBalanceUsd: null,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: result.volume_24h || null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    // Dispatch token selection event for widgets
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token }
    });
    window.dispatchEvent(event);
    
    console.log(`🎯 Ultimate Token Search: Selected ${result.symbol} (${result.mint})`);
    
    // Call callback if provided
    onSelectToken?.(token);
    
    // Close search
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setShowAdvancedFilters(false);
    setCurrentPage(1);
  };

  const formatPrice = (price: number | string | undefined) => {
    if (!price || price === 'N/A') return 'N/A';
    
    // Convert to number if it's a string
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice) || numPrice === 0) return '$0.00';
    
    // Handle very small numbers with scientific notation
    if (numPrice < 0.0001) return `$${numPrice.toExponential(1)}`;
    if (numPrice < 0.01) return `$${numPrice.toFixed(4)}`;
    if (numPrice < 1) return `$${numPrice.toFixed(3)}`;
    if (numPrice < 1000) return `$${numPrice.toFixed(2)}`;
    if (numPrice >= 1000000) return `$${(numPrice / 1000000).toFixed(1)}M`;
    if (numPrice >= 1000) return `$${(numPrice / 1000).toFixed(1)}K`;
    return `$${numPrice.toFixed(2)}`;
  };

  const formatMarketCap = (cap: number | string | undefined) => {
    if (!cap || cap === 'N/A') return 'N/A';
    
    // Convert to number if it's a string
    const numCap = typeof cap === 'string' ? parseFloat(cap) : cap;
    if (isNaN(numCap) || numCap === 0) return '$0';
    
    if (numCap >= 1_000_000_000_000) return `$${(numCap / 1_000_000_000_000).toFixed(1)}T`;
    if (numCap >= 1_000_000_000) return `$${(numCap / 1_000_000_000).toFixed(1)}B`;
    if (numCap >= 1_000_000) return `$${(numCap / 1_000_000).toFixed(1)}M`;
    if (numCap >= 1_000) return `$${(numCap / 1_000).toFixed(0)}K`;
    if (numCap < 1) return `$${numCap.toFixed(2)}`;
    return `$${Math.round(numCap)}`;
  };

  const formatNumber = (num: number | string | undefined) => {
    if (!num) return 'N/A';
    
    // Convert to number if it's a string
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue) || numValue === 0) return '0';
    
    if (numValue >= 1_000_000) return `${(numValue / 1_000_000).toFixed(1)}M`;
    if (numValue >= 1_000) return `${(numValue / 1_000).toFixed(0)}K`;
    return Math.round(numValue).toLocaleString();
  };

  return (
    <div className="relative" ref={searchRef}>
      {/* Search Button */}
      <button
        onClick={() => setIsSearchOpen(!isSearchOpen)}
        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-900 hover:text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 hover:border-[#24c35b]/30 rounded-lg transition-all duration-200 min-w-[280px] justify-start"
        data-onboarding="navbar-search-button"
      >
        <Search size={16} />
        <span>Ultimate Token Search</span>
      </button>

      {/* Search Dropdown */}
      {isSearchOpen && (
        <div className="absolute top-full left-0 mt-2 w-[750px] bg-white/95 backdrop-blur-md border border-gray-200/50 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* Search Input */}
          <div className="p-4 border-b border-gray-200/30">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={handleInputChange}
                placeholder="Search tokens by name, symbol, or address..."
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:border-[#24c35b]/50 focus:ring-1 focus:ring-[#24c35b]/20"
                data-onboarding="navbar-search"
              />
            </div>
            
            {/* Search Controls Row */}
            <div className="flex items-center justify-between mt-3">
              <div className="flex items-center gap-2">
                {/* Advanced Filters Toggle */}
                <button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className="flex items-center gap-2 text-xs text-gray-600 hover:text-[#24c35b] transition-colors"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                  </svg>
                  Advanced Filters
                  <ChevronDown size={12} className={`transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
                </button>

                {/* Saved Searches Toggle */}
                <button
                  onClick={() => setShowSavedSearches(!showSavedSearches)}
                  className="flex items-center gap-1 text-xs text-gray-600 hover:text-[#24c35b] transition-colors"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                  </svg>
                  Saved ({savedSearches.length})
                  <ChevronDown size={12} className={`transition-transform ${showSavedSearches ? 'rotate-180' : ''}`} />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Save Current Search */}
                {hasActiveSearch() && (
                  <button
                    onClick={() => setShowSaveModal(true)}
                    className="px-2 py-1 bg-[#24c35b]/20 text-[#24c35b] text-xs rounded hover:bg-[#24c35b]/30 transition-colors"
                  >
                    Save Search
                  </button>
                )}

                {/* Search by Filters Button */}
                {!searchQuery.trim() && Object.values(filters).some(v => v !== '' && v !== 'liquidityUsd' && v !== 'desc' && v !== true && v !== '24h') && (
                  <button
                    onClick={handleSearchByFilters}
                    className="px-3 py-1 bg-[#24c35b] text-black text-xs rounded hover:bg-[#1eaa4e] transition-colors font-medium"
                  >
                    Search by Filters
                  </button>
                )}
                
                {(Object.values(filters).some(v => v !== '' && v !== 'liquidityUsd' && v !== 'desc' && v !== true && v !== '24h')) && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-[#24c35b] hover:text-gray-900 transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Saved Searches Panel */}
          {showSavedSearches && (
            <div className="p-3 border-b border-gray-200/30 bg-gray-50 max-h-48 overflow-y-auto custom-scrollbar">
              <div className="space-y-2">
                {savedSearches.length > 0 ? (
                  savedSearches.map((savedSearch) => (
                    <div key={savedSearch.id} className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-900 text-xs font-medium truncate">{savedSearch.name}</div>
                        <div className="text-gray-600 text-xs truncate">
                          {savedSearch.query && `"${savedSearch.query}"`}
                          {savedSearch.query && Object.values(savedSearch.filters).some(v => v !== '' && v !== 'liquidityUsd' && v !== 'desc' && v !== true && v !== '24h') && ' + '}
                          {Object.values(savedSearch.filters).some(v => v !== '' && v !== 'liquidityUsd' && v !== 'desc' && v !== true && v !== '24h') && 'filters'}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={() => loadSavedSearch(savedSearch)}
                          className="px-2 py-1 bg-[#24c35b]/20 text-[#24c35b] text-xs rounded hover:bg-[#24c35b]/30 transition-colors"
                        >
                          Load
                        </button>
                        <button
                          onClick={() => deleteSavedSearch(savedSearch.id)}
                          className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded hover:bg-red-500/30 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-600 text-xs">No saved searches yet</p>
                    <p className="text-gray-500 text-xs mt-1">Create a search and click "Save Search" to save it</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Save Search Modal */}
          {showSaveModal && (
            <div className="p-3 border-b border-gray-200/30 bg-gray-50">
              <div className="space-y-2">
                <label className="text-gray-900 text-xs font-medium">Save Search As:</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={saveSearchName}
                    onChange={(e) => setSaveSearchName(e.target.value)}
                    placeholder="Enter search name..."
                    className="flex-1 bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        saveCurrentSearch();
                      } else if (e.key === 'Escape') {
                        setShowSaveModal(false);
                        setSaveSearchName('');
                      }
                    }}
                    autoFocus
                  />
                  <button
                    onClick={saveCurrentSearch}
                    disabled={!saveSearchName.trim()}
                    className="px-3 py-1 bg-[#24c35b] text-black text-xs rounded hover:bg-[#1eaa4e] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setShowSaveModal(false);
                      setSaveSearchName('');
                    }}
                    className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="p-3 border-b border-gray-200/30 bg-gray-50 max-h-64 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-3">
                {/* Date Filters - Top Priority */}
                <div className="col-span-2 space-y-2 mb-3 pb-3 border-b border-gray-200/30">
                  <h4 className="text-xs font-medium text-[#24c35b] mb-1">Creation Date Range</h4>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">From Date:</label>
                      <input
                        type="datetime-local"
                        value={filters.minCreatedAt}
                        onChange={(e) => handleFilterChange('minCreatedAt', e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">To Date:</label>
                      <input
                        type="datetime-local"
                        value={filters.maxCreatedAt}
                        onChange={(e) => handleFilterChange('maxCreatedAt', e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  {/* Quick Date Presets */}
                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={() => {
                        const now = new Date();
                        const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
                        handleFilterChange('minCreatedAt', oneHourAgo.toISOString().slice(0, 16));
                        handleFilterChange('maxCreatedAt', now.toISOString().slice(0, 16));
                      }}
                      className="px-2 py-1 bg-[#24c35b]/20 text-[#24c35b] text-xs rounded hover:bg-[#24c35b]/30 transition-colors"
                    >
                      Last Hour
                    </button>
                    <button
                      onClick={() => {
                        const now = new Date();
                        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                        handleFilterChange('minCreatedAt', oneDayAgo.toISOString().slice(0, 16));
                        handleFilterChange('maxCreatedAt', now.toISOString().slice(0, 16));
                      }}
                      className="px-2 py-1 bg-[#24c35b]/20 text-[#24c35b] text-xs rounded hover:bg-[#24c35b]/30 transition-colors"
                    >
                      Last 24h
                    </button>
                    <button
                      onClick={() => {
                        const now = new Date();
                        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        handleFilterChange('minCreatedAt', oneWeekAgo.toISOString().slice(0, 16));
                        handleFilterChange('maxCreatedAt', now.toISOString().slice(0, 16));
                      }}
                      className="px-2 py-1 bg-[#24c35b]/20 text-[#24c35b] text-xs rounded hover:bg-[#24c35b]/30 transition-colors"
                    >
                      Last Week
                    </button>
                  </div>
                </div>

                {/* Financial Filters */}
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-[#24c35b] mb-1">Financial</h4>
                  
                  <div className="grid grid-cols-2 gap-1">
                    <input
                      type="text"
                      placeholder="Min Liquidity"
                      value={filters.minLiquidity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleFilterChange('minLiquidity', value);
                        }
                      }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Max Liquidity"
                      value={filters.maxLiquidity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleFilterChange('maxLiquidity', value);
                        }
                      }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-1">
                    <input
                      type="text"
                      placeholder="Min Market Cap"
                      value={filters.minMarketCap}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleFilterChange('minMarketCap', value);
                        }
                      }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Max Market Cap"
                      value={filters.maxMarketCap}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          handleFilterChange('maxMarketCap', value);
                        }
                      }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/50 transition-all focus:outline-none"
                    />
                  </div>

                  {/* Volume Filters with Timeframe */}
                  <div>
                    <select
                      value={filters.volumeTimeframe}
                      onChange={(e) => handleFilterChange('volumeTimeframe', e.target.value)}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none mb-1"
                    >
                      <option value="5m">5m Volume</option>
                      <option value="15m">15m Volume</option>
                      <option value="30m">30m Volume</option>
                      <option value="1h">1h Volume</option>
                      <option value="6h">6h Volume</option>
                      <option value="12h">12h Volume</option>
                      <option value="24h">24h Volume</option>
                    </select>
                    
                    <div className="grid grid-cols-2 gap-1">
                      <input
                        type="text"
                        placeholder={`Min ${filters.volumeTimeframe}`}
                        value={filters.minVolume}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleFilterChange('minVolume', value);
                          }
                        }}
                        className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder={`Max ${filters.volumeTimeframe}`}
                        value={filters.maxVolume}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleFilterChange('maxVolume', value);
                          }
                        }}
                        className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Transaction & Security Filters */}
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-[#24c35b] mb-1">Activity & Security</h4>
                  
                  <div className="grid grid-cols-2 gap-1">
                    <input
                      type="text"
                      placeholder="Min Buys"
                      value={filters.minBuys}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*$/.test(value)) {
                          handleFilterChange('minBuys', value);
                        }
                      }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Min Sells"
                      value={filters.minSells}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d*$/.test(value)) {
                          handleFilterChange('minSells', value);
                        }
                      }}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                    />
                  </div>

                  <select
                    value={filters.market}
                    onChange={(e) => handleFilterChange('market', e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                  >
                    <option value="">All Markets</option>
                    <option value="pumpfun">Pump.fun</option>
                    <option value="raydium">Raydium</option>
                    <option value="meteora-dlmm">Meteora DLMM</option>
                    <option value="meteora-dyn">Meteora Dynamic</option>
                    <option value="raydium-clmm">Raydium CLMM</option>
                  </select>

                  <input
                    type="text"
                    placeholder="LP Burn %"
                    value={filters.lpBurn}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        handleFilterChange('lpBurn', value);
                      }
                    }}
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                  />

                  <select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1 text-gray-900 text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50 transition-all focus:outline-none"
                  >
                    <option value="liquidityUsd">Liquidity</option>
                    <option value="marketCapUsd">Market Cap</option>
                    <option value="volume_24h">24h Volume</option>
                    <option value="holders">Holders</option>
                    <option value="createdAt">Creation Date</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Search Results */}
          <div className="max-h-80 overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <div className="p-6 text-center text-gray-600">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#24c35b]"></div>
                <div className="mt-3 text-sm">Searching tokens...</div>
              </div>
            ) : searchResults.length > 0 ? (
              <div>
                {/* Pagination Controls - Top */}
                {(currentPage > 1 || hasNextPage) && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200/50">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage <= 1}
                      className={`px-3 py-1.5 text-xs rounded transition-colors ${
                        currentPage <= 1
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-[#24c35b]/20 text-[#24c35b] hover:bg-[#24c35b]/30'
                      }`}
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span>Page {currentPage}</span>
                      <span>•</span>
                      <span>{Math.ceil(totalResults / RESULTS_PER_PAGE)} pages</span>
                      <span>•</span>
                      <span>{totalResults} total</span>
                    </div>
                    
                    <button
                      onClick={handleNextPage}
                      disabled={!hasNextPage}
                      className={`px-3 py-1.5 text-xs rounded transition-colors ${
                        !hasNextPage
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-[#24c35b]/20 text-[#24c35b] hover:bg-[#24c35b]/30'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}

                {/* Results Header */}
                <div className="px-4 py-2 bg-white border-b border-gray-200/50 text-xs text-gray-600">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-3">Token</div>
                    <div className="col-span-2 text-right">Price</div>
                    <div className="col-span-2 text-right">Market Cap</div>
                    <div className="col-span-1 text-right">Holders</div>
                    <div className="col-span-2 text-center">Activity</div>
                    <div className="col-span-2 text-center">Links</div>
                  </div>
                </div>

                {/* Results List */}
                <div className="divide-y divide-gray-200/30">
                  {searchResults.map((result, index) => {
                    // Debug: Log the first result to see what data we have
                    if (index === 0) {
                      console.log('🔍 Search Result Data:', result);
                      console.log('Available fields:', Object.keys(result));
                    }
                    
                    return (
                      <button
                        key={result.mint}
                        onClick={() => handleSelectToken(result)}
                        className="w-full px-4 py-2 hover:bg-gray-50 transition-colors text-left"
                      >
                        <div className="grid grid-cols-12 gap-2 items-center">
                          {/* Token Info - Col 1-3 */}
                          <div className="col-span-3 flex items-center gap-2 min-w-0">
                            {result.image ? (
                              <img
                                src={result.image}
                                alt={result.symbol}
                                className="w-6 h-6 rounded-full bg-gray-700 flex-shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                  ((e.target as HTMLImageElement).nextElementSibling as HTMLElement)!.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div className="w-6 h-6 rounded-full bg-[#24c35b]/20 flex items-center justify-center text-[#24c35b] text-xs font-bold flex-shrink-0" style={{display: result.image ? 'none' : 'flex'}}>
                              {result.symbol?.substring(0, 2).toUpperCase()}
                            </div>
                            
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1 mb-0.5">
                                <span className="font-medium text-gray-900 text-sm truncate">{result.symbol}</span>
                                {result.verified && (
                                  <div className="w-3 h-3 rounded-full bg-[#24c35b] flex items-center justify-center flex-shrink-0">
                                    <span className="text-black text-xs">✓</span>
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-gray-600 truncate">{result.name}</div>
                              {result.market && (
                                <div className="text-xs text-gray-500 capitalize">{result.market}</div>
                              )}
                            </div>
                          </div>

                          {/* Price - Col 4-5 */}
                          <div className="col-span-2 text-right">
                            <div className="text-sm font-medium text-[#24c35b] truncate">
                              {(() => {
                                const formatted = formatPrice(result.priceUsd);
                                console.log('Price formatting:', result.priceUsd, '→', formatted);
                                return formatted;
                              })()}
                            </div>
                          </div>

                          {/* Market Cap - Col 6-7 */}
                          <div className="col-span-2 text-right">
                            <div className="text-sm text-gray-900 truncate">
                              {(() => {
                                const formatted = formatMarketCap(result.marketCapUsd);
                                console.log('Market Cap formatting:', result.marketCapUsd, '→', formatted);
                                return formatted;
                              })()}
                            </div>
                          </div>

                          {/* Holders - Col 8 */}
                          <div className="col-span-1 text-right">
                            <div className="text-xs text-gray-700 truncate">
                              {formatNumber(result.holders)}
                            </div>
                          </div>

                          {/* Activity (Buys/Sells) - Col 9-10 */}
                          <div className="col-span-2 text-center">
                            <div className="flex items-center justify-center gap-1 text-xs">
                              {result.totalBuys ? (
                                <span className="text-green-400 truncate">
                                  {formatNumber(result.totalBuys)}B
                                </span>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                              {result.totalBuys && result.totalSells && (
                                <span className="text-gray-500">/</span>
                              )}
                              {result.totalSells ? (
                                <span className="text-red-400 truncate">
                                  {formatNumber(result.totalSells)}S
                                </span>
                              ) : !result.totalBuys ? (
                                <span className="text-gray-500">-</span>
                              ) : null}
                            </div>
                          </div>

                          {/* Social Links - Col 11-12 */}
                          <div className="col-span-2 flex items-center justify-center gap-1">
                            {result.website && (
                              <div className="w-3 h-3 rounded bg-blue-500/20 flex items-center justify-center">
                                <svg width="6" height="6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-400">
                                  <circle cx="12" cy="12" r="10"/>
                                  <path d="M2 12h20"/>
                                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                              </div>
                            )}
                            {result.twitter && (
                              <div className="w-3 h-3 rounded bg-sky-500/20 flex items-center justify-center">
                                <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor" className="text-sky-400">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                              </div>
                            )}
                            {result.telegram && (
                              <div className="w-3 h-3 rounded bg-blue-400/20 flex items-center justify-center">
                                <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor" className="text-blue-300">
                                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                </svg>
                              </div>
                            )}
                            {result.discord && (
                              <div className="w-3 h-3 rounded bg-purple-500/20 flex items-center justify-center">
                                <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor" className="text-purple-400">
                                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                                </svg>
                              </div>
                            )}
                            {!result.website && !result.twitter && !result.telegram && !result.discord && (
                              <span className="text-xs text-gray-500">-</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : searchQuery.trim() && !isLoading ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                No tokens found for "{searchQuery}"
                {Object.values(filters).some(v => v !== '' && v !== 'liquidityUsd' && v !== 'desc' && v !== true && v !== '24h') && (
                  <div className="mt-2 text-xs">
                    Try adjusting your filters or search term
                  </div>
                )}
              </div>
            ) : !searchQuery.trim() ? (
              <div className="p-6 text-center text-gray-400 text-sm">
                <div className="mb-2">Search tokens by name or use filters</div>
                <div className="text-xs text-gray-500">
                  Type to search by name/symbol, or apply filters to discover tokens by metrics
                </div>
                {Object.values(filters).some(v => v !== '' && v !== 'liquidityUsd' && v !== 'desc' && v !== true && v !== '24h') && (
                  <div className="mt-3">
                    <button
                      onClick={handleSearchByFilters}
                      className="px-4 py-2 bg-[#24c35b] text-black text-sm rounded hover:bg-[#1eaa4e] transition-colors font-medium"
                    >
                      Search by Applied Filters
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

interface FeaturesMegaMenuProps {
  onClose: () => void;
  isHomePage?: boolean;
  onLaunchPlatform?: () => void;
  onNavigateToPage?: (page: 'about' | 'features' | 'team' | 'network' | 'careers' | 'giveaways' | 'docs' | 'showcase' | 'community' | 'roadmap' | 'partners' | 'whitepaper' | 'tokenomics') => void;
}

const FeaturesMegaMenu: React.FC<FeaturesMegaMenuProps> = ({ onClose, isHomePage = true, onLaunchPlatform, onNavigateToPage }) => {
  return (
    <div className="bg-white border border-gray-200/30 rounded-2xl overflow-hidden shadow-xl w-[720px]">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200/20 px-5 py-4">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png" 
              alt="EKKO Logo" 
              className="h-8 w-auto"
            />
            <div>
              <div className="text-sm text-gray-600 text-center">Everything you need to get started</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section - Three Columns */}
      <div className="bg-white px-0 py-0">
        <div className="grid grid-cols-3 divide-x divide-gray-200/30">
          {/* Left Column */}
          <div className="flex flex-col divide-y divide-gray-200/30">
            <button 
              className="flex items-center p-4 hover:bg-gray-100/50 transition-colors group text-left min-h-[80px]"
              onClick={() => {
                onNavigateToPage?.('roadmap');
                onClose();
              }}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-8 h-8 rounded-lg bg-[#24c35b]/10 flex items-center justify-center border border-[#24c35b]/20 flex-shrink-0">
                  <Rocket size={16} className="text-[#24c35b]" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium text-sm text-gray-900 text-left whitespace-nowrap">Roadmap</h4>
                </div>
              </div>
            </button>
            
            <button 
              className="flex items-center p-4 hover:bg-gray-100/50 transition-colors group text-left min-h-[80px]"
              onClick={() => {
                onNavigateToPage?.('partners');
                onClose();
              }}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-8 h-8 rounded-lg bg-[#24c35b]/10 flex items-center justify-center border border-[#24c35b]/20 flex-shrink-0">
                  <Users size={16} className="text-[#24c35b]" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium text-sm text-gray-900 text-left whitespace-nowrap">Partners</h4>
                </div>
              </div>
            </button>
          </div>

          {/* Middle Column */}
          <div className="flex flex-col divide-y divide-gray-200/30">
            <button 
              className="flex items-center p-4 hover:bg-gray-100/50 transition-colors group text-left min-h-[80px]"
              onClick={() => {
                onNavigateToPage?.('whitepaper');
                onClose();
              }}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-8 h-8 rounded-lg bg-[#24c35b]/10 flex items-center justify-center border border-[#24c35b]/20 flex-shrink-0">
                  <Book size={16} className="text-[#24c35b]" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium text-sm text-gray-900 text-left whitespace-nowrap">Whitepaper</h4>
                </div>
              </div>
            </button>
            
            <button 
              className="flex items-center p-4 hover:bg-gray-100/50 transition-colors group text-left min-h-[80px]"
              onClick={() => {
                onNavigateToPage?.('tokenomics');
                onClose();
              }}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-8 h-8 rounded-lg bg-[#24c35b]/10 flex items-center justify-center border border-[#24c35b]/20 flex-shrink-0">
                  <Coins size={16} className="text-[#24c35b]" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium text-sm text-gray-900 text-left whitespace-nowrap">Tokenomics</h4>
                </div>
              </div>
            </button>
          </div>

          {/* Right Column */}
          <div className="flex flex-col divide-y divide-gray-200/30">
            <button 
              className="flex items-center p-4 transition-colors group hover:bg-[#24c35b]/5 text-left min-h-[80px]"
              onClick={() => {
                onNavigateToPage?.('features');
                onClose();
              }}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-8 h-8 rounded-lg bg-[#24c35b]/20 flex items-center justify-center border border-[#24c35b]/30 flex-shrink-0">
                  <Target size={16} className="text-[#24c35b]" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium text-sm text-gray-900 text-left whitespace-nowrap">Features</h4>
                </div>
              </div>
            </button>
            
            <button 
              className="flex items-center p-4 transition-colors group hover:bg-[#24c35b]/5 text-left min-h-[80px]"
              onClick={() => {
                onNavigateToPage?.('team');
                onClose();
              }}
            >
              <div className="flex items-center gap-3 w-full">
                <div className="w-8 h-8 rounded-lg bg-[#24c35b]/20 flex items-center justify-center border border-[#24c35b]/30 flex-shrink-0">
                  <Users size={16} className="text-[#24c35b]" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-medium text-sm text-gray-900 text-left whitespace-nowrap">Team</h4>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
      {/* Gei no Chi - Land of Art - Full Width Section */}
      <div className="bg-white border-t border-b border-gray-20/20">
        <button 
          className="w-full relative overflow-hidden hover:bg-gray-50 transition-colors group text-left px-6 py-4"
          onClick={() => {
            onNavigateToPage?.('showcase');
            onClose();
          }}
        >
          {/* Warrior Background at 15% opacity */}
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-15 transition-opacity group-hover:opacity-20"
            style={{
              backgroundImage: 'url("https://raw.githubusercontent.com/TokentruthAI/TokenTruthLogos/refs/heads/main/214.png")'
            }}
          />
          
          {/* Content */}
          <div className="relative z-10 flex items-center justify-center gap-4">
            <div className="text-center">
              <h4 className="font-bold text-lg text-gray-900 mb-1">Gei no Chi - Land of Art</h4>
              <p className="text-sm text-gray-600">EKKO community showcase</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-[#24c35b]/10 flex items-center justify-center border border-[#24c35b]/20 flex-shrink-0">
              <svg className="w-5 h-5 text-[#24c35b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </button>
      </div>

      {/* Footer Section */}
      <div className="px-4 py-3 border-t border-gray-200/20 bg-white flex justify-between items-center">
        <div className="flex items-center gap-4">
          <a 
            href="https://t.me/ekkoplatform" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#24c35b] transition-colors"
          >
            <MessageSquare size={16} className="text-[#24c35b]" />
            Join our Telegram
          </a>
          <a 
            href="https://x.com/ekkoplatform" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#24c35b] transition-colors"
          >
            <svg className="w-4 h-4 text-[#24c35b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Follow us on X
          </a>
        </div>
        <button 
          onClick={() => {
            onLaunchPlatform?.();
            onClose();
          }}
          className="text-sm py-2 px-4 flex items-center gap-2 rounded-lg bg-[#24c35b] text-white hover:bg-[#1eaa4e] transition-colors font-medium shadow-md"
        >
          <Rocket size={14} />
          Launch Platform
        </button>
      </div>
    </div>
  );
};

// MegaMenu component for About dropdown
const AboutMegaMenu = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className="py-5 px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-3 text-primary">About Us</h3>
          <ul className="space-y-2">
            <li>
              <a 
                href="#about" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <Users size={14} />
                </div>
                <span>Our Team</span>
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <Book size={14} />
                </div>
                <span>Our Story</span>
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <Gift size={14} />
                </div>
                <span>Careers</span>
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <HelpCircle size={14} />
                </div>
                <span>FAQ</span>
              </a>
            </li>
          </ul>
        </div>
        
        <div className="md:col-span-2 bg-primary bg-opacity-5 rounded-xl p-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary bg-opacity-20 flex items-center justify-center flex-shrink-0">
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png" 
                alt="Ekko Logo" 
                className="h-6 w-auto"
              />
            </div>
            <div>
              <h4 className="text-base font-medium mb-2">Founded in 2025</h4>
              <p className="text-gray-300 text-xs mb-3">
                Built on the vision of creating seamless, beautiful interfaces 
                that transform how people interact with technology. Our team is dedicated 
                to pushing the boundaries of what's possible.
              </p>
              <a 
                href="#about" 
                className="inline-block px-3 py-1 bg-primary text-dark-grey rounded-lg text-xs font-medium hover:bg-opacity-90 transition-colors"
                onClick={onClose}
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// More mega menu content options
const PricingMegaMenu = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className="py-5 px-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-3 text-primary">Pricing Plans</h3>
          <ul className="space-y-2">
            <li>
              <a 
                href="#pricing" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <span className="font-semibold text-xs">S</span>
                </div>
                <div>
                  <span className="block font-medium">Starter</span>
                  <span className="text-xs text-gray-400">$29/month</span>
                </div>
              </a>
            </li>
            <li>
              <a 
                href="#pricing" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <span className="font-semibold text-xs">P</span>
                </div>
                <div>
                  <span className="block font-medium">Professional</span>
                  <span className="text-xs text-gray-400">$99/month</span>
                </div>
              </a>
            </li>
            <li>
              <a 
                href="#pricing" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <span className="font-semibold text-xs">E</span>
                </div>
                <div>
                  <span className="block font-medium">Enterprise</span>
                  <span className="text-xs text-gray-400">Custom pricing</span>
                </div>
              </a>
            </li>
          </ul>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-3 text-primary">Resources</h3>
          <ul className="space-y-2">
            <li>
              <a 
                href="#pricing" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <BarChart3 size={14} />
                </div>
                <span>Feature Comparison</span>
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <Settings size={14} />
                </div>
                <span>Usage Calculator</span>
              </a>
            </li>
            <li>
              <a 
                href="#" 
                className="flex items-center gap-2 text-white hover:text-primary transition-colors group text-sm"
                onClick={onClose}
              >
                <div className="p-1.5 rounded-full bg-primary bg-opacity-10 text-primary group-hover:bg-opacity-20 transition-colors">
                  <Gift size={14} />
                </div>
                <span>Special Offers</span>
              </a>
            </li>
          </ul>
        </div>
        
        <div className="bg-primary bg-opacity-10 rounded-xl p-4">
          <h3 className="text-base font-medium mb-2">Need Custom Pricing?</h3>
          <div className="mb-3 w-8 h-8 rounded-full bg-primary bg-opacity-20 flex items-center justify-center">
            <CreditCard size={16} className="text-primary" />
          </div>
          <p className="text-gray-300 text-xs mb-3">
            Talk to our sales team about enterprise-grade solutions tailored to your needs.
          </p>
          <a 
            href="#contact" 
            className="inline-block px-3 py-1 bg-primary text-dark-grey rounded-lg text-xs font-medium hover:bg-opacity-90 transition-colors"
            onClick={onClose}
          >
            Contact Sales
          </a>
        </div>
      </div>
    </div>
  );
};

interface NavbarProps {
  className?: string;
  onLaunchPlatform?: () => void;
  walletAddress?: string;
  walletProvider?: string;
  isHomePage?: boolean;
  isAccessPage?: boolean;
  isContentPage?: boolean;
  currentView?: 'trade' | 'trade2' | 'portfolio' | 'wallets' | 'settings' | 'community';
  onViewChange?: (view: 'trade' | 'trade2' | 'portfolio' | 'wallets' | 'settings' | 'community') => void;
  onShowConnectModal: () => void;
  onDisconnectWallet?: () => void;
  onGoHome?: () => void;
  onNavigateToPage?: (page: 'about' | 'features' | 'team' | 'network' | 'careers' | 'giveaways' | 'docs' | 'showcase' | 'community' | 'roadmap' | 'partners' | 'whitepaper' | 'tokenomics') => void;
  hideWalletButton?: boolean;
  isOnPlatformPage?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ 
  className = '', 
  onLaunchPlatform, 
  walletAddress,
  walletProvider,
  isHomePage = true,
  isAccessPage = false,
  isContentPage = false,
  currentView,
  onViewChange,
  onShowConnectModal,
  onDisconnectWallet,
  onGoHome,
  onNavigateToPage,
  hideWalletButton,
  isOnPlatformPage = false
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenu(null);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setActiveMenu(null);
  };

  const toggleMenu = (e: React.MouseEvent, menuId: string) => {
    e.stopPropagation();
    setActiveMenu(activeMenu === menuId ? null : menuId);
  };

  // Navigation items removed - using megamenu navbar consistently across platform
  const navItems: Array<{ label: string; value: string; icon: any; locked: boolean }> = [
    // Removed all navigation items - will use megamenu navbar consistently
  ];

  // Fix the background styling for nav items - remove glows
  const navItemClass = (itemView: string) => `
    flex items-center gap-2 px-4 pt-2 pb-1.5 rounded-lg transition-all duration-200 text-sm
    ${currentView === itemView
      ? 'bg-[#24c35b]/20 text-[#24c35b]'
      : 'hover:bg-white/5'}
    ${navItems.find(item => item.value === itemView)?.locked ? 'opacity-60 cursor-not-allowed' : ''}
  `;

  // Skip showing navigation items when on the Access page
  const shouldShowNavItems = !isHomePage && !isAccessPage && !isContentPage;

  return (
    <nav 
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'py-1.5' : 'py-2.5'
      } ${className} bg-dark-grey/90 backdrop-blur-md`}
      ref={menuRef}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo and Search Section */}
          <div className="flex items-center gap-4">
            {/* Logo - Always navigates to homepage */}
            <div 
              className="flex items-center cursor-pointer"
              onClick={() => {
                if (onGoHome) {
                  onGoHome();
                  console.log("Navigating to homepage");
                } else {
                  console.warn("onGoHome function is not defined");
                }
              }}
            >
              <img 
                src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png" 
                alt="Ekko Logo" 
                className="h-8 w-auto"
              />
            </div>
            
            {/* Ultimate Token Search - Only show on platform page */}
            {isOnPlatformPage && (
              <UltimateTokenSearch />
            )}
          </div>

          <div className="hidden md:flex items-center">
            {/* Right side buttons */}
            <div className="flex items-center gap-2">
              {/* Explore Button - Now shown on all pages for consistent navigation */}
              <div className="relative">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Close wallet dropdown when opening explore menu
                    window.dispatchEvent(new CustomEvent('ekko:close_all_dropdowns'));
                    setActiveMenu(activeMenu === 'features' ? null : 'features');
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-900 glassmorphism border border-green-200/50 rounded-lg transition-colors"
                >
                  <Rocket size={14} className="text-[#24c35b]" />
                  <span>Explore</span>
                  <ChevronDown size={14} className={`transition-transform text-[#24c35b] ${activeMenu === 'features' ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Features Dropdown */}
                {activeMenu === 'features' && (
                  <div 
                    className="absolute top-full -left-32 mt-2 z-50"
                    style={{ maxHeight: '500px' }}
                  >
                    <FeaturesMegaMenu 
                      onClose={() => setActiveMenu(null)} 
                      isHomePage={isHomePage}
                      onLaunchPlatform={onLaunchPlatform}
                      onNavigateToPage={onNavigateToPage}
                    />
                  </div>
                )}
              </div>
                
              {/* Wallet Connect Button */}
              {!hideWalletButton && (
                <WalletConnection 
                  walletAddress={walletAddress}
                  walletProvider={walletProvider}
                  onShowConnectModal={onShowConnectModal}
                  onDisconnectWallet={onDisconnectWallet}
                  onGoHome={onGoHome}
                  isOnPlatformPage={isOnPlatformPage}
                  onDropdownOpen={() => setActiveMenu(null)}
                />
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setMobileMenuOpen(!mobileMenuOpen);
                setActiveMenu(null);
              }}
              className="text-black focus:outline-none"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu dropdown - Beautiful design with EKKO logo and explore items */}
      <div 
        className={`
          md:hidden fixed top-full left-0 w-full bg-white shadow-2xl border-t border-gray-200 z-40
          transition-all duration-300 transform overflow-hidden
          ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10 pointer-events-none'}
        `}
        style={{ 
          height: mobileMenuOpen ? 'calc(100vh - 60px)' : '0px',
          top: '60px'
        }}
      >
        {/* Architectural corner decorations */}
        <div className="absolute top-0 left-0 w-6 h-6">
          <div className="absolute top-0 left-0 w-full h-1 bg-[#24c35b]"></div>
          <div className="absolute top-0 left-0 w-1 h-full bg-[#24c35b]"></div>
        </div>
        <div className="absolute top-0 right-0 w-6 h-6">
          <div className="absolute top-0 right-0 w-full h-1 bg-[#24c35b]"></div>
          <div className="absolute top-0 right-0 w-1 h-full bg-[#24c35b]"></div>
        </div>
        
        {/* Architectural lines */}
        <div className="absolute top-16 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#24c35b]/30 to-transparent"></div>
        
        <div className="h-full overflow-y-auto max-w-4xl mx-auto px-6 py-6 flex flex-col space-y-4">
          
          {/* EKKO Logo Section */}
          <div className="flex justify-center mb-2">
            <img 
              src="https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/EkkoLogo3%20(2).png" 
              alt="EKKO Logo" 
              className="h-12 w-auto"
            />
          </div>
          
                     {/* Mobile Menu Items */}
           <div className="space-y-3">
             <button 
               onClick={() => {
                 onNavigateToPage?.('team');
                 setMobileMenuOpen(false);
               }}
               className="w-full bg-gradient-to-r from-[#24c35b] to-[#1eaa4e] text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
             >
               <Users size={16} />
               Team
             </button>
             
             <button 
               onClick={() => {
                 onNavigateToPage?.('features');
                 setMobileMenuOpen(false);
               }}
               className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 px-6 rounded-xl hover:border-[#24c35b] hover:text-[#24c35b] transition-all duration-300 flex items-center justify-center gap-2"
             >
               <Rocket size={16} />
               Features
             </button>
             
             <button 
               onClick={() => {
                 onNavigateToPage?.('showcase');
                 setMobileMenuOpen(false);
               }}
               className="w-full bg-gradient-to-r from-[#24c35b] to-[#1eaa4e] text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
             >
               <BarChart3 size={16} />
               Showcase
             </button>
             
             <button 
               onClick={() => {
                 onNavigateToPage?.('whitepaper');
                 setMobileMenuOpen(false);
               }}
               className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 px-6 rounded-xl hover:border-[#24c35b] hover:text-[#24c35b] transition-all duration-300 flex items-center justify-center gap-2"
             >
               <HelpCircle size={16} />
               Whitepaper
             </button>
             
             <button 
               onClick={() => {
                 onNavigateToPage?.('tokenomics');
                 setMobileMenuOpen(false);
               }}
               className="w-full bg-gradient-to-r from-[#24c35b] to-[#1eaa4e] text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
             >
               <Coins size={16} />
               Tokenomics
             </button>
             
             <button 
               onClick={() => {
                 onNavigateToPage?.('roadmap');
                 setMobileMenuOpen(false);
               }}
               className="w-full bg-gradient-to-r from-[#24c35b] to-[#1eaa4e] text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
             >
               <Target size={16} />
               Roadmap
             </button>
             
             <button 
               onClick={() => {
                 onNavigateToPage?.('partners');
                 setMobileMenuOpen(false);
               }}
               className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 px-6 rounded-xl hover:border-[#24c35b] hover:text-[#24c35b] transition-all duration-300 flex items-center justify-center gap-2"
             >
               <Users size={16} />
               Partners
             </button>
           </div>
          
          {/* Social Links Section */}
          <div className="pt-4 border-t border-gray-200">
            <div className="flex justify-center gap-4">
              <a 
                href="https://t.me/ekkoplatform" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#24c35b] transition-colors"
              >
                <MessageSquare size={16} className="text-[#24c35b]" />
                Telegram
              </a>
              <a 
                href="https://x.com/ekkoplatform" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#24c35b] transition-colors"
              >
                <svg className="w-4 h-4 text-[#24c35b]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X (Twitter)
              </a>
            </div>
          </div>
          
          {/* Launch Platform Button */}
          {onLaunchPlatform && (
            <div className="pt-4 border-t border-gray-200">
              <button 
                onClick={() => {
                  onLaunchPlatform();
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-[#24c35b] to-[#1eaa4e] text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 text-lg"
              >
                <Rocket size={18} />
                LAUNCH PLATFORM
              </button>
            </div>
          )}
          
          {/* Mobile wallet connection button */}
          {!hideWalletButton && (
            <div className="pt-2">
              <button 
                onClick={() => {
                  onShowConnectModal && onShowConnectModal();
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-white border-2 border-gray-300 text-gray-700 font-bold py-3 px-6 rounded-xl hover:border-[#24c35b] hover:text-[#24c35b] transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Wallet size={16} />
                {walletAddress ? formatAddress(walletAddress) : "Connect Wallet"}
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

// Helper function to format wallet addresses
const formatAddress = (address: string): string => {
  if (!address) return '';
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

export default Navbar;